import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

const navLinks = [
  { href: "#accueil", label: "Accueil" },
  { href: "#race", label: "Le Sibérien" },
  { href: "#elevage", label: "Notre Élevage" },
  { href: "#chats", label: "Nos Chats" },
  { href: "#alumni", label: "Adoptés" },
  { href: "#portees", label: "Portées" },
  { href: "#blog", label: "Blog" },
  { href: "#temoignages", label: "Avis" },
  { href: "#contact", label: "Contact" },
];

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-cream/95 backdrop-blur-md shadow-soft border-b border-sand/30"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#accueil"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#accueil");
            }}
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <span className="text-2xl">🐱</span>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-lg text-forest leading-tight">
                La Taïga Siberia
              </span>
              <span className="text-xs text-forest/70 font-body">
                Élevage familial
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="nav-link font-body font-medium text-sm text-forest hover:text-autumn transition-colors"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-forest hover:bg-sand/50"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream border-sand w-72">
                <div className="flex flex-col gap-6 mt-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🐱</span>
                    <span className="font-heading font-bold text-xl text-forest">
                      La Taïga Siberia
                    </span>
                  </div>
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(link.href);
                      }}
                      className="font-body font-medium text-lg text-forest hover:text-autumn transition-colors py-2 border-b border-sand/30"
                      data-testid={`mobile-nav-${link.href.slice(1)}`}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
